// src/scenes/adminSwitch/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import Header from "components/Header";
import { gql, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { login as loginAction } from "state/authSlice";
import { useNavigate } from "react-router-dom";

const GET_GROUPS = gql`
  query {
    groups {
      id
      name
      description
      logoPath
      isActive
    }
  }
`;

const decodeJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(json);
};

const AdminSwitch = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const roleGroupId = localStorage.getItem("roleGroupId");
  const token = localStorage.getItem("token");
  const isSysAdmin = roleGroupId === "1";

  useEffect(() => {
    if (!isSysAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [isSysAdmin, navigate]);

  const { data, loading, refetch } = useQuery(GET_GROUPS, {
    skip: !isSysAdmin,
    fetchPolicy: "cache-and-network",
  });

  const groups = useMemo(() => {
    const rows = data?.groups || [];
    return [...rows].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [data]);

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [switching, setSwitching] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSwitch = async () => {
    setErrorMsg("");

    if (!selectedGroupId) {
      setErrorMsg("Please select a group first.");
      return;
    }
    if (!token) {
      setErrorMsg("Missing token. Please login again.");
      navigate("/login", { replace: true });
      return;
    }

    try {
      setSwitching(true);

      const resp = await fetch(process.env.REACT_APP_ADMIN_SWITCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Id: Number(selectedGroupId) }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Switch failed (${resp.status})`);
      }

      const result = await resp.json(); // { token, expireDate, groupLogoPath, ... }

      if (!result?.token) {
        throw new Error("Backend did not return a token.");
      }

      // ✅ Replace token
      localStorage.setItem("token", result.token);

      // ✅ Decode updated token and store groupName/groupId for UI
      try {
        const payload = decodeJwt(result.token);

        const groupId = payload.GroupId ?? payload.groupId;
        const groupName = payload.Group ?? payload.group ?? payload.GroupName ?? payload.groupName;

        if (groupId != null) localStorage.setItem("groupId", String(groupId));
        if (groupName) localStorage.setItem("groupName", String(groupName));
        window.dispatchEvent(new Event("centra:group-switched"));

        if (result.groupLogoPath) {
          localStorage.setItem("groupLogoPath", String(result.groupLogoPath));
        }
      } catch (e) {
        console.warn("JWT decode after switch failed:", e);
      }

      // ✅ Update Redux auth state (token changed)
      dispatch(loginAction({ token: result.token }));

      navigate("/dashboard", { replace: true });
    } catch (e) {
      console.error(e);
      setErrorMsg(
        typeof e?.message === "string"
          ? e.message
          : "Failed to switch group. Please try again."
      );
    } finally {
      setSwitching(false);
    }
  };

  if (!isSysAdmin) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.alt} 60%, ${theme.palette.background.default} 100%)`,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 900 }}>
        <Header title="ADMIN SWITCH" subtitle="Select a group to enter as a member" />

        <Card
          sx={{
            mt: 2,
            borderRadius: 3,
            boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                <LockOutlined />
              </Box>

              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1.1}>
                  CENTRA Admin Portal
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
                  Switch into any chamaa workspace using a temporary token update.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2.5, opacity: 0.35 }} />

            {errorMsg ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            ) : null}

            <Box sx={{ display: "grid", gap: 2 }}>
              <FormControl fullWidth disabled={loading || switching}>
                <InputLabel id="group-select-label">Select Group</InputLabel>
                <Select
                  labelId="group-select-label"
                  label="Select Group"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                >
                  {groups.map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {g.name} {!g.isActive ? "(Inactive)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={handleSwitch}
                  disabled={loading || switching || !selectedGroupId}
                  sx={{ borderRadius: 2, py: 1.1, fontWeight: 800 }}
                >
                  {switching ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      Switching...
                    </Box>
                  ) : (
                    "Enter Group Workspace"
                  )}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => refetch()}
                  disabled={loading || switching}
                  sx={{ borderRadius: 2, py: 1.1, fontWeight: 700 }}
                >
                  Refresh Groups
                </Button>
              </Box>

              {loading ? (
                <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                  Loading groups...
                </Typography>
              ) : null}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminSwitch;
